import { UserPhotoSize, useUserPhotoQuery } from "@/common/api/user/use-user-photo.query";
import { cn } from "@/common/helpers/utils";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useState } from "react";
import { Loader, LoaderSize } from "../ui/loader";

interface AvatarLightboxDialogProps {
  userAzureObjectUuid: string;
  alt: string;
  trigger: React.ReactNode;
}

export function AvatarLightboxDialog({ userAzureObjectUuid, alt, trigger }: AvatarLightboxDialogProps) {
  const [open, setOpen] = useState(false);

  const { data: largeSrc, isLoading } = useUserPhotoQuery({
    userAzureObjectUuid: open ? userAzureObjectUuid : null,
    size: UserPhotoSize.Size648x648,
  });

  const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div className="inline-flex" onClick={stopAll} onPointerDown={stopAll} onPointerUp={stopAll}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            className={cn("cursor-pointer rounded-full transition-opacity hover:opacity-80")}
            aria-label={alt}
          >
            {trigger}
          </button>
        </DialogTrigger>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className={cn(
              "data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-50",
              "bg-black/60 backdrop-blur-sm duration-300 ease-out",
            )}
            onClick={() => setOpen(false)}
          />
          <DialogPrimitive.Content
            data-focus-indicator="none"
            className={cn(
              "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 fixed",
              "top-[50%] left-[50%] z-50 translate-x-[-50%] translate-y-[-50%] border-0 bg-transparent p-0",
              "shadow-none duration-300 ease-out outline-none focus:outline-none",
            )}
          >
            <DialogPrimitive.Title className="sr-only">{alt}</DialogPrimitive.Title>
            <div className="relative">
              {isLoading ? (
                <div className="bg-background/50 flex h-80 w-80 items-center justify-center rounded-xl">
                  <Loader size={LoaderSize.Medium} />
                </div>
              ) : (
                <img src={largeSrc ?? undefined} alt={alt} className="max-h-80 max-w-80 rounded-xl object-contain" />
              )}
              <DialogPrimitive.Close
                className={cn(
                  "absolute -top-3 -right-3 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full",
                  "bg-black/60 text-white transition-colors hover:bg-black/80",
                )}
                aria-label="Close"
              >
                <X className="size-4" />
              </DialogPrimitive.Close>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </Dialog>
    </div>
  );
}
